<template>
  <div>
    <Breadcrumbs v-if="showBreadcrumbs" :breadcrumbs-info="breadcrumbs" :ignore-current="true"></Breadcrumbs>
    <b-modal
      :visible="!!warning"
      id="warning"
      centered
      hide-footer
      hide-header
      size="lg"
      @hide="onCloseWarning()"
    >
      <b-row class="alert-messages">
        <div class="alert-container" v-bind:class="{'mt-0': !session}">
          <div>{{warning}}</div>
        </div>
      </b-row>
      <b-link block class="centered-block close-button" variant="secondary" @click="closeWarning()">&times;</b-link>
    </b-modal>
    <b-modal
      :visible="!!callCenterPopupContent"
      id="call-center-popup"
      centered
      hide-footer
      hide-header
      size="lg"
      @hide="onCloseCallCenterPopup()"
    >
      <template v-if="!!callCenterPopupContent">
        <div v-html="callCenterPopupContent"></div>
      </template>
      <b-link block class="centered-block close-button" variant="secondary" @click="closeCallCenterPopup()">&times;</b-link>
    </b-modal>
    <SessionContainer v-if="!callCenterPopupContent">
      <div id="choices" class="flex">
        <template v-if="!isSessionValid">
          <SessionComplete>You have been logged out due to inactivity.</SessionComplete>
        </template>
        <template v-else-if="needsCustomerMatch()">
          <CustomerMatch @back="goBack" @done="updateInputState(true)" @fail="onCustomerMatchFailed"></CustomerMatch>
        </template>
        <template v-else-if="needsCustomerVerification()">
          <CustomerVerify @back="goBack" @done="updateInputState(true)" @fail="onCustomerMatchFailed"></CustomerVerify>
        </template>
        <template v-else-if="needsRMAItems()">
          <SelectActionItems
            label="Submit"
            :defaults="inputData"
            :input="selectedInput"
            @select="selectItemsForRMAOrCancel"
            @back="goBack"
          ></SelectActionItems>
        </template>
        <template v-else-if="needsRebillItems()">
          <SelectActionItems
            rebill
            label="Submit"
            :defaults="inputData"
            :input="selectedInput"
            @select="selectItemsForRMAOrCancel"
            @back="goBack"
          ></SelectActionItems>
        </template>
        <template v-else-if="needsVariants()">
          <Variants
            :item="currentSelectedItem"
            :new-campaign-product="selectedCampaignProduct"
            :quantity="(inputData && inputData['quantity']) ? inputData['quantity'] : currentSelectedItem.quantity"
            @select="selectVariants"
            @back="goBack"
          ></Variants>
        </template>
        <template v-else-if="isItemStatus">
          <OrderItems
            :key="'session-' + selectedInput.id.toString()"
            :input="selectedInput"
            :items="currentSelectedItem ? [currentSelectedItem] : []"
            @submit="selectInput"
            @back="goBack"
            @edit="editItem"
          >
            <SessionContent></SessionContent>
          </OrderItems>
        </template>
        <template v-else-if="isCheckout">
          <Checkout @submit="submitInputAndData" @back="goBack"></Checkout>
        </template>
        <template v-else-if="isInputActionStatus">
          <InputActionStatus :input="selectedInput" @done="submit" @back="goBack" @edit="editItem"></InputActionStatus>
        </template>
        <template v-else-if="isCheckoutConfirm">
          <CheckoutConfirm :input="selectedInput" @done="submit" @back="goBack"></CheckoutConfirm>
        </template>
        <template v-else-if="isSessionComplete">
          <SessionComplete><div v-html="thankYouMessage"></div></SessionComplete>
        </template>
        <template v-else-if="isCampaignSelect">
          <SelectCampaign :input="selectedInput" @select="submitInputData" @back="goBack"></SelectCampaign>
        </template>
        <template v-else-if="isSelectedProductRequired">
          <SelectExchangeProduct :item="selectedItem" :check-price="checkExchangeablePrice" @select="submitInputAndData" @back="goBack"></SelectExchangeProduct>
        </template>
        <template v-else-if="isItemSelection">
          <div>
            <SessionContent></SessionContent>
            <SelectItems
              label="Continue"
              item-select-label="Get Help"
              item-select-icon="angle-right"
              :defaults="selectedItems"
              :input="selectedInput"
              @select="onItemsSelected"
              @back="goBack"
            ></SelectItems>
          </div>
        </template>
        <template v-else-if="isOrderTracking">
          <Tracking :fulfillment="fulfillments.length > 0 ? fulfillments[0] : null" :nextInput="session.valid_inputs.length > 0 ? session.valid_inputs[0].id : null"></Tracking>
        </template>
        <template v-else>
          <div class="inputs" v-if="canDisplayTitle">
            <StepTitle>
              <div v-if="sessionTitle" v-html="sessionTitle" class="how-can"></div>
              <template v-if="session && !session.enhanced_mode" #subtitle><div v-html="session.subtitle" class="option"></div></template>
              <template v-if="session && session.content && session.content.length" #content>
                <div class="page-content" v-html="session.content"></div>
              </template>
              <template v-if="session && !session.enhanced_mode && selectedItems.length && currentSelectedItem" #product>
                <table class="selected-item-table selected-item">
                  <tr>
                    <td v-if="selectedItems.length > 1">{{selectedItemIndex + 1}}/{{selectedItems.length}}</td>
                    <td><img :src="currentSelectedItemImageUrl" alt=""></td>
                    <td class="product-name">{{currentSelectedItem.quantity}} &times; <span v-html="currentSelectedItem.name"></span></td>
                  </tr>
                </table>
              </template>
            </StepTitle>
            <template v-if="session && (!session.enhanced_mode || !session.content)">
              <div v-if="needsInputFields()">
                <InputFields @back="goBack" :fields="selectedInput.input_fields" :item="selectedItem" @submit="submitInputData"></InputFields>
              </div>
              <div v-else-if="isCustomerInfo">
                <CustomerInfo :input="selectedInput" @submit="selectInput" @back="goBack"></CustomerInfo>
              </div>
              <div v-else-if="isPaymentUpdater">
                <PaymentUpdater :rebill="rebill" :input="selectedInput" @submit="selectInput" @back="goBack"></PaymentUpdater>
              </div>
              <div v-else-if="isPaymentInfo">
                <PaymentInfo :input="selectedInput" @submit="selectInput" @back="goBack"></PaymentInfo>
              </div>
              <div v-else-if="isRadioGroup || isSelect || isRadioOrSelect">
                <ChoiceFields @submit="selectInput" @back="goBack" :key="session ? session.step : 0"></ChoiceFields>
              </div>
              <div v-else-if="isButtonGroup">
                <div v-if="isIconButtons" class="home-icons" :class="{'wrapper': isBoxButton}">
                  <b-row class="justify-content-sm-center boxes icon-buttons">
                    <template v-if="isBoxButton">
                      <b-col cols="6" sm="12" v-for="input in session.valid_inputs" :key="'mob-choice-' + input.id" @click="selectInput(input)" class="choice d-block d-sm-none">
                        <div class="box" :class="getCustomClass(input)" v-lazy-container="{ selector: 'img' }">
                          <img :data-src="input.icon.file" alt="" block variant="primary"/>
                          <h2 v-html="input.formatted_label"></h2>
                        </div>
                      </b-col>
                    </template>
                    <b-col cols="12" sm="auto" v-for="input in session.valid_inputs" :key="'choice-' + input.id" @click="selectInput(input)" class="choice" :class="{'d-none d-sm-block': isBoxButton}">
                      <div class="box hide-on-phone" :class="getCustomClass(input)" v-lazy-container="{ selector: 'img' }">
                        <img :data-src="input.icon.file" alt="" block variant="primary"/>
                        <h2 v-html="input.formatted_label"></h2>
                      </div>
                      <b-button block
                                class="d-sm-none btn btn-default btn-session gutters mob-home-btn"
                                :class="getCustomClass(input)"
                                variant="primary">
                        <img :src="input.icon.file" alt="" block variant="primary" class="mob-home-btn-icon"/>
                        <p v-html="input.formatted_label" class="new-btns"></p>
                      </b-button>
                    </b-col>
                  </b-row>
                </div>
                <div v-else>
                  <b-row class="justify-content-md-center boxes claim">
                    <b-col :md="singleColumnButtons ? 6 : 10" :lg="singleColumnButtons ? 6 : 8" xl="6" class="my-class">
                      <b-row>
                        <b-col v-for="input in session.valid_inputs" :key="'choice-' + input.id" :md="singleColumnButtons ? 12 : 6" class="text-center">
                          <b-link v-if="isLink(input)"
                                  @click="selectInput(input)"
                                  class="q_links step-link"
                                  :class="getCustomClass(input)"
                                  v-html="input.formatted_label"
                          ></b-link>
                          <b-button v-else block
                                    @click="selectInput(input)"
                                    class="btn btn-default btn-session faq_btn update-info"
                                    :class="getCustomClass(input)"
                                    variant="primary"
                          ><div v-html="input.formatted_label"></div></b-button>
                        </b-col>
                      </b-row>
                    </b-col>
                  </b-row>
                </div>
                <StepBackButton @back="goBack" />
              </div>
              <div v-else>
                <b-row class="justify-content-md-center boxes welcome">
                  <b-col v-if="session && session.can_undo" md="4" lg="3">
                    <b-button block @click="goBack()" variant="outline-primary" class="btn-back">Back</b-button>
                  </b-col>
                  <b-col md="4" lg="3" class="mt-2 mt-md-0">
                    <b-button block @click="submitForm()" variant="primary" class="btn-account">Submit</b-button>
                  </b-col>
                </b-row>
              </div>
            </template>
            <template v-else>
                <b-row v-if="session && session.can_undo" class="justify-content-md-center boxes welcome">
                  <b-col md="4" lg="3">
                    <b-button block @click="goBack()" variant="outline-primary" class="btn-back">Back</b-button>
                  </b-col>
                </b-row>
            </template>
          </div>
        </template>
      </div>
    </SessionContainer>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState, mapGetters } from 'vuex'
import { SessionStatus, InputType, InputState, VariantType, MobileIconButtonType } from '../constants'
import { getClassFromObject, getItemImageUrl, getErrorData, isFirstInputDone, setFirstInputDone } from '../utils'

const StepTitle = () => import('./StepTitle')
const SessionContent = () => import('./SessionContent')
const SessionContainer = () => import('./SessionContainer')
const SessionComplete = () => import('@/components/SessionComplete')
const OrderItems = () => import('@/components/OrderItems')
const CustomerInfo = () => import('@/components/CustomerInfo')
const PaymentInfo = () => import('@/components/PaymentInfo')
const PaymentUpdater = () => import('@/components/PaymentUpdater')
const SelectItems = () => import('@/components/SelectItems')
const CustomerMatch = () => import('@/components/CustomerMatch')
const CustomerVerify = () => import('@/components/CustomerVerify')
const InputActionStatus = () => import('@/components/InputActionStatus')
const InputFields = () => import('@/components/InputFields')
const SelectExchangeProduct = () => import('@/components/SelectExchangeProduct')
const ChoiceFields = () => import('@/components/ChoiceFields')
const SelectActionItems = () => import('@/components/SelectActionItems')
const SelectCampaign = () => import('@/components/SelectCampaign')
const Variants = () => import('@/components/Variants')
const Checkout = () => import('@/components/Checkout')
const CheckoutConfirm = () => import('@/components/CheckoutConfirm')
const Tracking = () => import('@/components/Tracking')
const Breadcrumbs = () => import('@/components/Breadcrumbs')

const StepBackButton = () => import('@/components/StepBackButton')

export default {
  name: 'Session',
  components: {
    StepBackButton,
    Breadcrumbs,
    SessionContainer,
    SessionContent,
    StepTitle,
    SessionComplete,
    OrderItems,
    CustomerInfo,
    PaymentInfo,
    PaymentUpdater,
    SelectItems,
    CustomerMatch,
    CustomerVerify,
    InputActionStatus,
    InputFields,
    SelectExchangeProduct,
    ChoiceFields,
    SelectActionItems,
    SelectCampaign,
    Variants,
    Checkout,
    CheckoutConfirm,
    Tracking
  },
  data: function() {
    return {
      selectedInput: null,
      userSelectedInput: null,
      selectedItem: null,
      updatedItem: null,
      isItemSelection: false,
      inputType: InputType.Choice,
      submitting: false,
      inputData: null,
      statusMessages: [],
      isSelectedProductRequired: false,
      callCenterPopupContent: null,
      itemSelectionState: InputState.Unknown,
      checkForVariants: false,
      selectedCampaignProduct: null,
      warning: null,
      rebill: false,
      hasSlotContent: false,
      breadcrumbs: null,
      checkExchangeablePrice: false
    }
  },
  computed: {
    ...mapState(['session', 'firstLogin', 'selectedItems', 'selectedItemIndex', 'inputState', 'refreshSessionOnUndo', 'styleVariables', 'isLoading', 'fulfillments']),
    ...mapGetters(['canGotoPreviousItem', 'currentSelectedItem', 'isSessionValid', 'isSessionComplete', 'isLifelineActive']),
    sessionTitle() {
      if (this.session && this.firstLogin && this.session.welcome_message && this.session.welcome_message.length) {
        return this.session.welcome_message
      } else if (this.session && !this.session.enhanced_mode) {
        return this.session.label
      }

      return null
    },
    canDisplayTitle: function() {
      return !this.isFAQs && !this.isProductInfo
    },
    isFAQs: function() {
      return (this.inputType === InputType.Faqs || (this.session && this.session.step_type === InputType.Faqs))
    },
    isCustomerInfo: function() {
      return ([InputType.BillingAddress, InputType.ShippingAddress].indexOf(this.inputType) >= 0)
    },
    isPaymentInfo: function() {
      return (this.inputType === InputType.PaymentInfo && !this.rebill)
    },
    isPaymentUpdater: function() {
      return this.inputType === InputType.PaymentInfo && this.rebill
    },
    isItemStatus: function() {
      return (this.inputType === InputType.ItemStatus)
    },
    isProductInfo: function() {
      return (this.inputType === InputType.Product)
    },
    isInputActionStatus: function() {
      return (this.inputType === InputType.InputActionStatus)
    },
    isCheckout: function() {
      return (this.inputType === InputType.Checkout || (this.session && this.session.step_type === InputType.Checkout))
    },
    isCheckoutConfirm: function() {
      return (this.inputType === InputType.CheckoutConfirm)
    },
    isRadioGroup: function() {
      return (this.inputType === InputType.Radio)
    },
    isSelect: function() {
      return (this.inputType === InputType.Select)
    },
    isRadioOrSelect: function() {
      return (this.inputType === InputType.RadioSelect)
    },
    isButtonGroup: function() {
      return (this.inputType === InputType.Choice)
    },
    isCampaignSelect: function() {
      return (this.inputType === InputType.SelectCampaign)
    },
    isCustomerMatch: function() {
      return (this.inputType === InputType.MatchCustomer)
    },
    isOrderTracking: function() {
      return this.session && this.session.step_type === InputType.ShipmentTracking
    },
    currentSelectedItemImageUrl: function() {
      if (this.currentSelectedItem) {
        return getItemImageUrl(this.currentSelectedItem, 'small')
      }

      return 'https://via.placeholder.com/100x100?text=No+Image'
    },
    isIconButtons: function() {
      if (this.session.valid_inputs.length) {
        for (let i = 0; i < this.session.valid_inputs.length; i++) {
          if (!this.session.valid_inputs[i].icon) {
            return false
          }
        }

        return true
      }

      return false
    },
    hasLink: function() {
      if (this.session.valid_inputs.length) {
        for (let i = 0; i < this.session.valid_inputs.length; i++) {
          if (this.session.valid_inputs[i].type === InputType.Link) {
            return true
          }
        }
      }

      return false
    },
    singleColumnButtons: function() {
      return (this.session.valid_inputs.length < 6) || this.hasLink
    },
    thankYouMessage: function() {
      if (this.session && this.session.thank_you_message && this.session.thank_you_message.length) {
        return this.session.thank_you_message
      }

      return 'You have been logged out.'
    },
    isInputDataEditable: function() {
      let isEditable = false

      if (this.selectedInput && this.selectedInput.input_fields) {
        const fields = Object.values(this.selectedInput.input_fields)

        for (const field of fields) {
          if (field.type !== InputType.Hidden) {
            isEditable = true
            break
          }
        }
      }

      return isEditable
    },
    isBoxButton: function() {
      const mobileIconButtonType = (this.session && this.session.mobile_icon_button_type) ||
        (this.styleVariables && this.styleVariables.mobile_icon_button_type)

      if (mobileIconButtonType) {
        return +mobileIconButtonType === MobileIconButtonType.Box
      }
      return false
    },
    showBreadcrumbs: function() {
      return this.isCustomerInfo || this.isPaymentInfo || this.isUpdateInfoStep()
    }
  },
  methods: {
    isUpdateInfoStep: function(session) {
      session = session || this.session
      if (!session || !session.step_name) {
        return false
      }
      return ['update your info', 'update your information'].indexOf(session.step_name.toLowerCase()) > -1
    },
    populateBreadcrumbs: function(session) {
      if (this.showBreadcrumbs) {
        this.breadcrumbs = [
          {label: 'Main Menu', path: {name: 'session'}, home: true},
          {label: 'Update Your Info', path: {}, back: this.goBack, current: this.isUpdateInfoStep(session)}
        ]
        if (!this.isUpdateInfoStep(session)) {
          this.breadcrumbs.push({label: session.label, path: {}, home: false, current: true})
        }
      }
    },
    closeCallCenterPopup: function() {
      this.$bvModal.hide('call-center-popup')
    },
    onCloseCallCenterPopup: function() {
      this.callCenterPopupContent = null

      if (this.isInputActionStatus) {
        this.submit()
      }
    },
    closeWarning: function() {
      this.$bvModal.hide('warning')
    },
    onCloseWarning: function() {
      // refresh the session data
      axios.get('/sessions/')
        .then(resp => {
          this.$store.commit('session', resp.data)
        })
        .catch(err => {
          this.$store.commit('critical_error', err)
        })
        .finally(() => this.init())
    },
    isLink: function(input) {
      return input.type === InputType.Link
    },
    getCustomClass: function(input) {
      return getClassFromObject(input)
    },
    selectInput: function(input, data, messages) {
      this.selectedInput = input
      if (data) {
        if (!data.updatePaymentInfo) {
          this.userSelectedInput = input
        }
        delete data['updatePaymentInfo']
      }

      if (typeof data !== 'undefined') {
        this.inputData = data
      }

      if (typeof messages !== 'undefined') {
        this.statusMessages = messages
      }

      console.log({input: input, data: data})

      this.submitForm()
    },
    submitInputAndData: function(input, data, selectedCampaignProduct) {
      this.selectedInput = input
      this.userSelectedInput = input
      this.selectedCampaignProduct = selectedCampaignProduct
      this.submitInputData(data)
    },
    clearInput: function() {
      if ((this.session && this.session.step_type === InputType.EnterTroubleshooter) &&
          (this.selectedInput && this.selectedInput.type === InputType.PathNotFound)) {
        // path not found (i.e. no troubleshooter exists for product) for troubleshooter required
        // data input, going back from the data input should skip back past the (selected) path not found input
        if (this.session.can_undo) {
          this.goBack()
        }
      } else {
        this.selectedInput = null
        this.userSelectedInput = null
        this.updateInputState(false)
      }
    },
    onItemsSelected: function(items) {
      this.$store.commit('set_selected_items', items)
      this.selectedItem = items[0]
      this.submit()
    },
    selectItemsForRMAOrCancel: function(data, selectedItem) {
      if (selectedItem) {
        this.selectedItem = selectedItem
      }

      this.itemSelectionState = InputState.Unknown
      this.submitInputData(data)
    },
    selectVariants: function(data) {
      if (this.inputData) {
        data = Object.assign({}, this.inputData, data)
      }

      this.checkForVariants = false
      this.selectedCampaignProduct = null
      this.isSelectedProductRequired = false
      this.checkExchangeablePrice = false
      this.submitInputData(data)
    },
    submitInputData: function(data) {
      this.inputData = data
      let mustSelectVariants = false

      if ((this.selectedCampaignProduct && (this.selectedCampaignProduct.product.variant_type !== VariantType.None)) ||
        (this.checkForVariants && this.currentSelectedItem && this.inputData &&
          (this.currentSelectedItem.product.variant_type !== VariantType.None))) {
        if (!this.inputData || !this.inputData['variants'] || !this.inputData['variants'].length) {
          mustSelectVariants = true
        }
      }

      this.checkForVariants = false

      if (this.itemSelectionState === InputState.RMAItems) {
        this.itemSelectionState = InputState.Unknown
        this.$store.commit('set_input_state', InputState.RMAItems)
      } else if (this.itemSelectionState === InputState.RebillItems) {
        this.itemSelectionState = InputState.Unknown
        this.$store.commit('set_input_state', InputState.RebillItems)
      } else if (mustSelectVariants) {
        this.$store.commit('set_input_state', InputState.Variants)
      } else {
        this.$store.commit('set_input_state', InputState.OkToSubmit)
        this.submit()
      }
    },
    submitForm: function() {
      if (this.selectedInput) {
        this.updateInputState(false)
        this.submit()
      }
    },
    editItem: function(input, item) {
      this.selectedInput = input
      this.userSelectedInput = input
      this.selectedItem = item
      this.submitForm()
    },
    onCustomerMatchFailed: function(message) {
      if (this.isCustomerMatch && this.session.additional_inputs && this.session.additional_inputs.length) {
        this.selectInput(this.session.additional_inputs[0]) // customer match step - select no customer found input
      } else {
        this.$store.commit('no_match', message)
      }
    },
    updateInputState: function(resubmit) {
      if (this.selectedInput) {
        let inputState = InputState.OkToSubmit

        if (this.selectedInput.requires_customer) {
          if (this.session && this.session.status === SessionStatus.Unmatched) {
            inputState = InputState.NeedsMatch
          } else if (this.session && ((this.session.status === SessionStatus.Unverified) ||
              (this.session.status === SessionStatus.Matched))) {
            inputState = InputState.NeedsVerification
          }
        }

        if (inputState === InputState.OkToSubmit) {
          this.itemSelectionState = InputState.Unknown
          this.checkForVariants = false

          if (this.selectedInput.input_fields) {
            const keys = Object.keys(this.selectedInput.input_fields)

            if (keys && keys.length) {
              for (const key of keys) {
                const fieldType = this.selectedInput.input_fields[key].type
                const defaultVal = this.selectedInput.input_fields[key].default

                if (key === 'variants') {
                  this.checkForVariants = true
                } else if ((key === 'items') && (fieldType === InputType.ActiveItem)) {
                  this.itemSelectionState = InputState.RMAItems // select items for RMA
                } else if ((key === 'items') && (fieldType === InputType.RebillItem)) {
                  this.itemSelectionState = InputState.RebillItems // select items for rebill

                  if (this.selectedInput.type === InputType.PaymentInfo) {
                    if (!this.userSelectedInput) {
                      inputState = InputState.OkToSubmit
                      this.rebill = true
                    } else {
                      inputState = InputState.RebillItems
                    }
                  }
                } else if ((fieldType !== InputType.Product) && (fieldType !== InputType.Hidden) && !defaultVal) {
                  if (!this.isRadioGroup && !this.isSelect && !this.isRadioOrSelect) {
                    inputState = InputState.InputFields // form input fields
                  }
                }
              }

              if (inputState === InputState.OkToSubmit) {
                if (this.itemSelectionState === InputState.RMAItems) {
                  inputState = InputState.RMAItems
                } else if (this.checkForVariants) {
                  if (this.currentSelectedItem &&
                    (this.currentSelectedItem.product.variant_type !== VariantType.None)) {
                    inputState = InputState.Variants
                  }
                }
              }
            }
          }
        } else {
          this.$store.commit('hide_welcome_message')
        }

        if ([InputState.NeedsMatch, InputState.NeedsVerification, InputState.RMAItems].indexOf(inputState) > -1 &&
          !this.refreshSessionOnUndo) {
          this.$store.commit('refresh_session_on_undo', true)
          if (!isFirstInputDone()) {
            history.pushState(null, null, '/undo')
            history.pushState(null, null, '/session')
          }
        }

        this.$store.commit('set_input_state', inputState)
        if (resubmit) {
          if (this.isOkToResubmit()) {
            console.log('resubmit')
            this.submit()
          } else {
            this.submitting = true // we selected an input, but not ok to submit yet (i.e. needs customer verification)
            this.$store.commit('ready')
          }
        }
      } else {
        this.$store.commit('set_input_state', InputState.Unknown)
      }

      if (!this.isLifelineActive && this.session && this.session.is_popup) {
        this.$store.commit('set_display_state', null)
      }
    },
    needsInputFields: function() {
      return (this.inputState === InputState.InputFields)
    },
    needsRMAItems: function() {
      return (this.inputState === InputState.RMAItems)
    },
    needsRebillItems: function() {
      return (this.inputState === InputState.RebillItems)
    },
    needsVariants: function() {
      return (this.inputState === InputState.Variants)
    },
    needsCustomerMatch: function() {
      return (this.inputState === InputState.NeedsMatch)
    },
    needsCustomerVerification: function() {
      return (this.inputState === InputState.NeedsVerification)
    },
    isReadyToSubmit: function() {
      return (this.inputState === InputState.OkToSubmit)
    },
    isOkToResubmit: function() {
      return this.isReadyToSubmit() && (this.isCustomerMatch || (this.session.valid_inputs.length !== 1))
    },
    getDefaultData: function() {
      let data = {}

      if (this.selectedInput.input_fields) {
        const keys = Object.keys(this.selectedInput.input_fields)

        if (keys && keys.length) {
          for (const key of keys) {
            if ('default' in this.selectedInput.input_fields[key]) {
              data[key] = this.selectedInput.input_fields[key].default
            }
          }
        }
      }

      return data
    },
    getShipmentReturnLabel: function(qrCode) {
      if (this.currentSelectedItem) {
        this.$store.commit('busy')
        this.$store.dispatch('load_shipment_return_label', {item: this.currentSelectedItem, qr_code: qrCode})
      }
    },
    submit: function() {
      if (!this.isReadyToSubmit() || this.session.is_step_preview) {
        this.submitting = true // we selected an input, but not ok to submit yet (i.e. needs customer verification)
        this.$store.commit('ready')
      } else if (this.selectedInput.type === InputType.ShipmentReturnLabel) {
        this.getShipmentReturnLabel(false)
      } else if (this.selectedInput.type === InputType.ShipmentReturnQRCode) {
        this.getShipmentReturnLabel(true)
      } else {
        let params = {
          input: this.selectedInput.id
        }

        if (this.selectedItem) {
          params['item'] = this.selectedItem.id
        }

        if (this.inputData) {
          params['data'] = Object.assign(this.getDefaultData(), this.inputData)
        }

        this.$store.commit('busy')
        this.$store.dispatch('input', params)
          .then(session => {
            this.$store.commit('update_selected_item')
            console.log(session.step)

            // if we were passed some status messages to display, commit them so that they show up
            if (this.statusMessages && this.statusMessages.length) {
              this.$store.commit('messages', this.statusMessages)
            }
            if (this.selectedInput && this.selectedInput.type === InputType.Checkout) {
              this.$store.commit('set_cart', [])
            }

            if (!isFirstInputDone() && !this.refreshSessionOnUndo) {
              history.pushState(null, null, '/undo')
              let path = '/session'
              if (this.isFAQs) {
                path = ''
              }
              history.pushState(null, null, path)
              setFirstInputDone()
            } else if (this.refreshSessionOnUndo) {
              this.$store.commit('refresh_session_on_undo', false)
              setFirstInputDone()
            }

            this.init() // refresh

            // scroll to top on each input
            document.body.scrollTop = 0 // For Safari
            document.documentElement.scrollTop = 0 // For Chrome, Firefox, IE and Opera
          })
          .catch(err => {
            console.log(err)
            this.updateInputState(false)

            if (err.response && err.response.status === 400) {
              // handle input field validation errors
              const error = getErrorData(err)
              console.log(error)

              if (error.data && error.data.refresh_data) {
                this.warning = 'Sorry, your session has expired.  Please refresh the page and try again.'
              } else if (this.needsInputFields()) {
                console.log('needs input fields')
                this.$store.commit('error', error.errors)
              } else if (this.selectedInput && this.selectedInput.type === InputType.Checkout) {
                this.$store.commit('checkout_error', error.errors)
              } else {
                this.$store.commit('critical_error', err)
                this.init()
              }
            } else if (err.response && err.response.status === 402) { // payment required
              // popup payment form - input then needs to be resubmitted with input data {card: payment info}
              this.$store.commit('error', err) // remove this error and show a friendly message along with the payment form
            } else if (err.response && err.response.status === 422) { // non-critical error (more like a warning)
              this.$store.commit('error', err)
            } else {
              this.$store.commit('critical_error', err)
              this.init()
            }
          })
      }
    },
    goBack: function() {
      console.log('go back')

      if (this.session && this.session.is_step_preview) {
        return // ignore back button if this is a preview step
      }

      let canUndo = !this.submitting && this.session && this.session.can_undo

      if (canUndo) {
        this.$store.commit('busy')
        this.$store.dispatch('undo')
          .then(session => {
            this.$store.commit('clear_messages')
            this.$store.commit('increment_undo_count')
            this.init() // refresh
          })
          .catch(err => {
            console.log(err)
            if (!err.response || err.response.status !== 422) {
              this.$store.commit('critical_error', err)
            }
          })
      } else {
        this.$store.commit('increment_undo_count')
        this.$store.commit('clear_messages')
        this.init() // refresh
      }
    },
    init: function() {
      console.log('session init')
      this.$store.commit('busy')
      this.$store.commit('set_input_state', InputState.Unknown)
      this.inputType = InputType.Choice
      this.selectedInput = null
      this.userSelectedInput = null
      this.selectedItem = null
      this.updatedItem = null
      this.isItemSelection = false
      this.submitting = false
      this.inputData = null
      this.isSelectedProductRequired = false
      this.checkExchangeablePrice = false
      this.statusMessages = []
      this.itemSelectionState = InputState.Unknown
      this.checkForVariants = false
      this.selectedCampaignProduct = null
      this.warning = null

      if (this.session) {
        this.callCenterPopupContent = this.session.call_center_popup

        if (this.session.is_first_step) {
          this.$store.commit('clear_selected_items')
        }

        if (this.session.valid_inputs.length === 1) {
          // if there's only 1 valid input, select it by default
          this.selectedInput = this.session.valid_inputs[0]
          this.inputType = this.selectedInput.type

          if ([InputType.Item, InputType.ActiveItem].indexOf(this.selectedInput.type) >= 0) {
            this.isItemSelection = true
          }

          this.updateInputState(false)
        } else if (this.session.valid_inputs.length > 1) {
          // multiple inputs - get the type
          this.inputType = this.session.valid_inputs[0].type
        }

        // check if we have to select a product (i.e. revising an order)
        for (const input of this.session.valid_inputs) {
          const keys = Object.keys(input.input_fields)

          if (keys && keys.length) {
            const key = keys[0]
            const field = input.input_fields[key]

            if (field.type === InputType.Product) {
              this.isSelectedProductRequired = true
              this.checkExchangeablePrice = !!field.check_price
              break
            }
          }
        }

        if (this.isOrderTracking) {
          this.$store.commit('busy')
          this.$store.dispatch('load_fulfillments')
            .then(_ => {
              this.$store.commit('ready')
              setTimeout(() => this.$bvModal.show('fulfillment-tracking-modal', 100))
            })
        }

        // show/hide the lifeline popup or contact form
        if (this.session.show_lifeline_contact) {
          this.$bvModal.hide('lifeline')
          setTimeout(() => this.$bvModal.show('contact'))
        } else if (this.session.is_lifeline_active) {
          this.$bvModal.hide('contact')
          setTimeout(() => this.$bvModal.show('lifeline'))
        }

        this.populateBreadcrumbs(this.session)
      }

      this.$store.commit('ready')
    },
    initializeEnhanceContent: function() {
      const _this = this
      const pageContent = document.querySelector('.page-content')
      if (pageContent) {
        this.hasSlotContent = true
        // skip in case of step preview
        if (_this.session && !_this.session.is_step_preview) {
          // setup path inputs
          const pathInputs = Array.from(pageContent.querySelectorAll("[ctype='path-input']")) || []
          pathInputs.forEach(element => {
            const elemType = element.getAttribute('elemtype')
            if (elemType === 'button' || elemType === 'link') {
              const hasInput = _this.session.valid_inputs.some((validInput) => validInput.input_id === +element.getAttribute('input'))
              if (hasInput) {
                if (elemType === 'link') {
                  element.removeAttribute('target')
                  element.removeAttribute('href')
                }
                if (!element.callback) {
                  element.addEventListener('click', element.callback = (e) => {
                    const inputEl = e.target.closest('a[elemtype=button]') || e.target.closest('a[elemtype=link]')
                    const input = _this.session.valid_inputs.find((validInput) => validInput.input_id === +inputEl.getAttribute('input'))
                    _this.selectInput(input)
                    return false
                  })
                }
              } else {
                element.remove()
              }
            }
          })
        }

        if (pageContent.style) {
          pageContent.style.display = 'block'
        }

        // setup bac buttons
        const backBtns = Array.from(pageContent.querySelectorAll("[elemtype='back-button']")) || []
        backBtns.forEach(element => {
          element.addEventListener('click', (e) => {
            this.goBack()
            return false
          })
        })
      }
    }
  },
  watch: {
    '$store.state.session': function(to, from) {
      if (this.session && this.session.enhanced_mode) {
        setTimeout(() => this.initializeEnhanceContent(), 0)
      }
    }
  },
  created() {
    this.init()
    if (this.isFAQs) {
      this.$router.replace({name: 'faq-topics'})
    }
    if (this.isProductInfo) {
      this.$router.replace({name: 'products'})
    }
  },
  updated() {
    if (this.isFAQs) {
      setTimeout(() => {
        if (window.location.pathname !== '/topics') {
          this.$router.replace({name: 'faq-topics'})
        }
      }, 100)
    }
    if (this.isProductInfo) {
      this.$router.replace({
        name: 'products',
        params: {
          input: this.selectedInput,
          title: this.session.label,
          subtitle: this.session ? this.session.subtitle : '',
          submit: this.selectInput,
          back: this.goBack
        }
      })
    }

    if (!this.hasSlotContent && this.session && this.session.enhanced_mode) {
      setTimeout(() => this.initializeEnhanceContent(), 0)
    }
  },
  mounted: function mounted() {
    const vm = this
    vm.initializeEnhanceContent()
  }
}
</script>

<style scoped>
</style>
